import { KeyboardEvent, ReactNode, useEffect, useRef } from "react";
import type { UseFormRegisterReturn } from "react-hook-form";

import StarIcon from "@assets/icons/star.svg";
import CloseRoundedIcon from "@assets/icons/close-icon-rounded.svg";
import { APP_COLORS } from "@styles/colors";
import useSystemStore from "@store/useSystemStore";
import { cn } from "@utils/tailwind.util";

const iconButtonClassName =
  "flex cursor-pointer items-center text-gray-500 opacity-70 duration-200 hover:opacity-100 dark:text-invertedPrimary80";

interface DomainRowFormProps {
  field: UseFormRegisterReturn;
  error?: string;
  onCommit: () => void;
  trailing?: ReactNode;
  autoStartEditing?: boolean;
  showMakePrimary?: boolean;
  onMakePrimary?: () => void;
  onRemove?: () => void;
}

export function DomainRow({
  field,
  error,
  onCommit,
  trailing,
  autoStartEditing = false,
  showMakePrimary = false,
  onMakePrimary,
  onRemove,
}: DomainRowFormProps) {
  const { isDarkMode } = useSystemStore();
  const errorColor = isDarkMode ? APP_COLORS.content.errorLight : APP_COLORS.content.error;
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (autoStartEditing) inputRef.current?.focus();
  }, [autoStartEditing]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.currentTarget.blur();
    }
  };

  const showRowActions = Boolean(onMakePrimary || onRemove);

  return (
    <div className="flex w-full flex-col">
      <div
        className={cn(
          "group flex h-[38px] min-w-0 items-center gap-2 rounded-lg border bg-white px-2 dark:bg-gray-800",
          error
            ? "border-red-600"
            : cn(
                "border-stroke-primary dark:border-stroke-primaryDark",
                "hover:border-stroke-secondary dark:hover:border-stroke-secondaryDark",
                "focus-within:border-stroke-actionPrimary dark:focus-within:border-stroke-actionPrimaryDark",
                "focus-within:shadow-mainButtonFocus"
              )
        )}
      >
        <span className="flex-shrink-0 select-none text-sm text-gray-400">https://</span>
        <input
          {...field}
          ref={(node) => {
            field.ref(node);
            inputRef.current = node;
          }}
          type="text"
          placeholder="example.com"
          className={cn(
            "min-w-0 flex-1 border-0 bg-transparent p-0 text-sm font-medium text-primary-600 shadow-none",
            "focus:border-0 focus:outline-none focus:ring-0",
            "placeholder:font-normal placeholder:text-gray-400"
          )}
          onBlur={(event) => {
            void field.onBlur(event);
            onCommit();
          }}
          onKeyDown={handleKeyDown}
        />
        {trailing}

        {showRowActions && (
          <div className="invisible flex flex-shrink-0 items-center gap-1.5 group-hover:visible group-focus-within:visible">
            {onMakePrimary && showMakePrimary && (
              <button
                type="button"
                aria-label="Make primary"
                title="Make primary"
                onClick={onMakePrimary}
                className={iconButtonClassName}
              >
                <StarIcon className="h-4 w-4" />
              </button>
            )}
            {onRemove && (
              <span
                aria-label="Remove domain"
                title="Remove domain"
                onClick={onRemove}
                className="flex h-full cursor-pointer items-center opacity-70 duration-200 hover:opacity-100"
              >
                <CloseRoundedIcon width={16} height={16} color="#6B7080" />
              </span>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 px-1 text-[13px] text-error-600" style={{ color: errorColor }}>
          {error}
        </p>
      )}
    </div>
  );
}
